import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ArrowRight, Palette, Upload, UserPlus, Sparkles, Scale } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { restartAgentTour } from "../components/WelcomeTour";
import { US_STATES } from "../lib/usStates";

const PRESETS = ["#1F6FEB", "#059669", "#D97706", "#DC2626", "#7C3AED", "#0F172A"];

// Steps: 1) Brand, 2) Brokerage & Licensing (required), 3) Logo, 4) Lead, 5) Trial
const TOTAL_STEPS = 5;

export default function Onboarding() {
  const { agent, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [color, setColor] = useState("#1F6FEB");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  // Broker-disclosure required fields — advertising compliance blocks any
  // portal invite until every starred field is filled in.
  const [compliance, setCompliance] = useState({
    license_number: "",
    license_state: "",
    brokerage_legal_name: "",
    brokerage_license_number: "",
    office_street: "",
    office_city: "",
    office_state: "",
    office_zip: "",
  });

  useEffect(() => {
    if (agent) {
      setColor(agent.brand_color || "#1F6FEB");
      setName(agent.full_name || "");
      setCompliance((c) => ({
        ...c,
        license_number: agent.license_number || c.license_number,
        license_state: agent.license_state || agent.state || c.license_state,
        brokerage_legal_name: agent.brokerage_legal_name || c.brokerage_legal_name,
        brokerage_license_number: agent.brokerage_license_number || c.brokerage_license_number,
        office_street: agent.office_street || c.office_street,
        office_city: agent.office_city || c.office_city,
        office_state: agent.office_state || c.office_state,
        office_zip: agent.office_zip || c.office_zip,
      }));
    }
  }, [agent]);

  const complianceComplete =
    compliance.license_number.trim() &&
    compliance.brokerage_legal_name.trim() &&
    compliance.brokerage_license_number.trim() &&
    compliance.office_street.trim();

  const saveCompliance = async () => {
    if (!complianceComplete) {
      toast.error("Fill in every starred field to continue");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/settings", compliance);
      refresh();
      setStep(3);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const saveBrand = async () => {
    setBusy(true);
    try {
      await api.patch("/settings", { full_name: name, phone, brand_color: color });
      document.documentElement.style.setProperty("--primary-hex", color);
      refresh();
      setStep(2);
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const uploadLogo = async (file) => {
    setLogoUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); refresh(); }
    catch { toast.error("Logo upload failed"); }
    finally { setLogoUploading(false); }
  };

  const addLead = async () => {
    if (!leadName.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      await api.post("/leads", { full_name: leadName, phone: leadPhone, sms_consent: smsConsent, consent_source: smsConsent ? "onboarding capture" : null });
      setStep(5);
    } catch { toast.error("Add lead failed"); }
    finally { setBusy(false); }
  };

  /**
   * Free-tier activation — no credit card, no trial expiration. The
   * Agent row is on `plan_tier="free"` by default, which is permanent.
   * This handler just marks onboarding complete, kicks off the welcome
   * tour, and routes to `/`.
   */
  const startFreeTrial = () => {
    localStorage.setItem("realtor_os_onboarded", "1");
    // restartAgentTour clears the "seen" flag AND fires the CustomEvent
    // the SpotlightTour component listens for — so the tour opens even
    // when we're navigating within the same session without a reload.
    restartAgentTour();
    toast.success("Welcome to iKEY — you're on the Free tier");
    nav("/");
  };

  const skipToApp = () => { localStorage.setItem("realtor_os_onboarded", "1"); nav("/"); };

  const inputCls = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand";

  return (
    <div className="min-h-screen relative py-10 sm:py-16 px-4 sm:px-6">
      {/* Splash treatment — same luxury photo as login/reset. */}
      <div className="fixed inset-0 z-0" aria-hidden="true">
        <img
          src="https://images.unsplash.com/photo-1613977257363-707ba9348227?auto=format&fit=crop&w=1400&q=80"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/75 via-slate-950/45 to-slate-950/85" />
      </div>

      <div className="relative z-10 max-w-lg mx-auto">
        <div className="text-center mb-8 text-white">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <img src="/ikey-logo.png" alt="iKEY" className="h-8 w-8 rounded-lg ring-1 ring-white/25" />
            <span className="font-heading text-lg font-semibold tracking-tight drop-shadow">iKEY</span>
          </div>
          <h2 className="font-heading text-xl sm:text-2xl leading-tight font-bold tracking-tight drop-shadow-lg">
            The Only Key You Need <span className="bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent">from Lead to Sold.</span>
          </h2>
        </div>

        <div className="rounded-2xl bg-white/30 backdrop-blur-xl p-6 sm:p-8 shadow-2xl ring-1 ring-white/30">
          <div className="mb-6 flex items-center gap-1.5">
            {[1,2,3,4,5].map(n => (
              <div key={n} className={`h-1.5 flex-1 rounded ${n <= step ? "" : "bg-slate-200"}`} style={n <= step ? { background: "var(--primary-hex)" } : {}} />
            ))}
          </div>
          <div className="text-xs font-medium uppercase tracking-widest text-white/90 drop-shadow">Step {step} of {TOTAL_STEPS}</div>

        {step === 1 && (
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-6">
              <Palette className="h-6 w-6" style={{ color: "var(--primary-hex)" }} />
              <h1 className="font-heading text-3xl font-bold tracking-tighter text-white drop-shadow-md">Make it yours.</h1>
            </div>
            <p className="text-sm text-white/90 mb-8 drop-shadow">This is what your clients will see in their portal.</p>
            <div className="space-y-4">
              <div><label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">Full name</label><input required value={name} onChange={e => setName(e.target.value)} className={inputCls} data-testid="onb-name" /></div>
              <div><label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">Phone</label><input value={phone} onChange={e => setPhone(e.target.value)} className={inputCls} data-testid="onb-phone" /></div>
              <div>
                <label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">Brand color</label>
                <div className="mt-2 flex items-center gap-2">
                  <input type="color" value={color} onChange={e => setColor(e.target.value)} className="h-10 w-14 rounded cursor-pointer" />
                  <div className="flex gap-1.5 ml-2">
                    {PRESETS.map(c => (
                      <button key={c} type="button" onClick={() => setColor(c)} className={`h-9 w-9 rounded transition-transform ${color === c ? "ring-2 ring-offset-2 ring-slate-900 scale-110" : "ring-1 ring-slate-200 hover:scale-105"}`} style={{ background: c }} data-testid={`onb-color-${c.replace('#','')}`} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <button onClick={saveBrand} disabled={busy || !name} className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-md py-3 text-sm font-semibold text-white disabled:opacity-40" style={{ background: color }} data-testid="onb-next-1">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="h-6 w-6" style={{ color: "var(--primary-hex)" }} />
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter text-white drop-shadow-md">Brokerage &amp; licensing.</h1>
            </div>
            <p className="text-sm text-white/90 mb-6 drop-shadow">
              State real-estate rules require your employing broker to appear on every client-facing surface.
              These fields are required before you can send a portal invite. Only starred fields are required now — refine the rest in Settings later.
            </p>
            <div className="space-y-3 max-h-[52vh] overflow-y-auto pr-1">
              <div>
                <label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">Your license number *</label>
                <input value={compliance.license_number} onChange={e => setCompliance({ ...compliance, license_number: e.target.value })} className={inputCls} data-testid="onb-license-number" />
              </div>
              <div>
                <label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">Your license state</label>
                <select value={compliance.license_state} onChange={e => setCompliance({ ...compliance, license_state: e.target.value })} className={inputCls} data-testid="onb-license-state">
                  <option value="">— Select —</option>
                  {US_STATES.map(([code, nm]) => <option key={code} value={code}>{code} · {nm}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">Brokerage legal name *</label>
                <input value={compliance.brokerage_legal_name} onChange={e => setCompliance({ ...compliance, brokerage_legal_name: e.target.value })} className={inputCls} data-testid="onb-brokerage-legal-name" />
              </div>
              <div>
                <label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">Brokerage license number *</label>
                <input value={compliance.brokerage_license_number} onChange={e => setCompliance({ ...compliance, brokerage_license_number: e.target.value })} className={inputCls} data-testid="onb-brokerage-license" />
              </div>
              <div>
                <label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">Office street address *</label>
                <input value={compliance.office_street} onChange={e => setCompliance({ ...compliance, office_street: e.target.value })} className={inputCls} data-testid="onb-office-street" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">City</label>
                  <input value={compliance.office_city} onChange={e => setCompliance({ ...compliance, office_city: e.target.value })} className={inputCls} data-testid="onb-office-city" />
                </div>
                <div>
                  <label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">State</label>
                  <select value={compliance.office_state} onChange={e => setCompliance({ ...compliance, office_state: e.target.value })} className={inputCls} data-testid="onb-office-state">
                    <option value="">—</option>
                    {US_STATES.map(([code]) => <option key={code} value={code}>{code}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">ZIP</label>
                <input value={compliance.office_zip} onChange={e => setCompliance({ ...compliance, office_zip: e.target.value })} className={inputCls} data-testid="onb-office-zip" />
              </div>
            </div>
            <button onClick={saveCompliance} disabled={busy || !complianceComplete} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-md py-3 text-sm font-semibold text-white disabled:opacity-40" style={{ background: "var(--primary-hex)" }} data-testid="onb-next-2">
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-6">
              <Upload className="h-6 w-6" style={{ color: "var(--primary-hex)" }} />
              <h1 className="font-heading text-3xl font-bold tracking-tighter text-white drop-shadow-md">Add a logo.</h1>
            </div>
            <p className="text-sm text-white/90 mb-6 drop-shadow">Optional — but it makes the client portal feel like yours instead of a stock template.</p>
            {agent?.logo_url ? (
              <div className="mb-6 p-6 border border-slate-200 rounded-lg bg-white text-center">
                <img src={agent.logo_url} alt="" className="h-16 mx-auto" />
                <p className="mt-3 text-sm text-emerald-700 font-semibold">Logo saved</p>
              </div>
            ) : (
              <label className="block cursor-pointer border-2 border-dashed border-slate-300 rounded-lg p-10 text-center bg-white hover:border-slate-400">
                <Upload className="h-8 w-8 mx-auto text-slate-400" />
                <p className="mt-3 font-heading font-semibold text-slate-900">Upload your logo</p>
                <p className="text-xs text-slate-500 mt-1">PNG, JPG, or SVG · under 4MB</p>
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && uploadLogo(e.target.files[0])} data-testid="onb-logo-input" />
              </label>
            )}
            <div className="mt-6 flex items-center justify-between gap-3">
              <button onClick={() => setStep(4)} className="text-sm text-white/85 hover:text-white drop-shadow">Skip for now</button>
              <button onClick={() => setStep(4)} disabled={logoUploading} className="rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="onb-next-3">Continue</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-6">
              <UserPlus className="h-6 w-6" style={{ color: "var(--primary-hex)" }} />
              <h1 className="font-heading text-3xl font-bold tracking-tighter text-white drop-shadow-md">Add your first lead.</h1>
            </div>
            <p className="text-sm text-white/90 mb-6 drop-shadow">Once one lead is in, Run My Day starts ranking. Skip if you'd rather import a CSV later.</p>
            <div className="space-y-4">
              <div><label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">Lead full name</label><input value={leadName} onChange={e => setLeadName(e.target.value)} className={inputCls} data-testid="onb-lead-name" /></div>
              <div><label className="text-xs font-medium text-white uppercase tracking-wider drop-shadow">Phone</label><input value={leadPhone} onChange={e => setLeadPhone(e.target.value)} className={inputCls} data-testid="onb-lead-phone" /></div>
              <label className="flex items-start gap-2 cursor-pointer p-3 rounded-md bg-slate-50 border border-slate-200">
                <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} className="mt-0.5" data-testid="onb-lead-sms-consent" />
                <span className="text-sm text-slate-800"><strong>SMS consent.</strong> The lead has given express written consent to automated text messages (TCPA).</span>
              </label>
            </div>
            <div className="mt-8 flex items-center justify-between gap-3">
              <button onClick={() => setStep(5)} className="text-sm text-white/85 hover:text-white drop-shadow">Skip · import CSV later</button>
              <button onClick={addLead} disabled={busy || !leadName} className="rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" style={{ background: "var(--primary-hex)" }} data-testid="onb-add-lead">Add lead</button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles className="h-6 w-6" style={{ color: "var(--primary-hex)" }} />
              <h1 className="font-heading text-3xl font-bold tracking-tighter text-white drop-shadow-md">You're on the permanently free CRM.</h1>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-6">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500">iKEY · Free Tier</div>
                  <div className="mt-2 font-heading text-3xl font-bold tracking-tight text-slate-900">Free — forever</div>
                  <div className="mt-1 text-sm text-slate-600">No credit card · no time limit · CRM only</div>
                </div>
                <CheckCircle2 className="h-6 w-6" style={{ color: "var(--primary-hex)" }} />
              </div>
              <ul className="mt-6 space-y-2 text-sm text-slate-700">
                {["Includes 1–3 Clients (CRM only — DocuSign not included on Free)",
                  "Unlimited leads and transactions",
                  "Client portal with your branding",
                  "AI-drafted daily briefings",
                  "Upgrade any time to unlock DocuSign and add more than 3 clients"].map(f => (
                  <li key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" style={{ color: "var(--primary-hex)" }} />{f}</li>
                ))}
              </ul>
              <div className="mt-4 text-xs text-slate-500 leading-relaxed border-t border-slate-100 pt-3">
                No trial countdown. When you're ready for more than 3 clients or e-signatures, upgrade from Settings.
              </div>
            </div>
            <div className="mt-8 flex items-center justify-between gap-3">
              <button onClick={skipToApp} className="text-sm text-white/85 hover:text-white drop-shadow" data-testid="onb-skip-billing">Skip for now</button>
              <button onClick={startFreeTrial} className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="onb-start-trial">
                Start using iKEY <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
